// Prazo de rede da camada de sync (AP-116, sonda AP3 do cybersec, 29/09/2026).
//
// Nenhum fetch do sync tinha prazo. Um pedido pendurado segurava o flush inteiro (o
// `flushInflight` nunca assentava, e nenhum outro flush rodava no run), e, pior, o
// flush retomava DEPOIS de um sign-out e de outra conta parear, com os grupos da conta
// anterior ainda em memória (ver o cabeçalho do flusher).
//
// O prazo é por CORRIDA (`Promise.race` contra `makeNetDeadline`, lib/deadline.ts), nunca
// só `AbortController`: na faixa Chr42-63 do fleet (a LG do dono é Chr53) o `fetch` é o
// nativo, que ignora `signal` em silêncio, e o abort sozinho nunca assenta o `await`. O
// `signal` vai junto, como extra, onde o motor o respeita. O prazo cobre o fetch E a
// leitura do corpo (um corpo que não chega também pendura).

import { makeNetDeadline } from '../lib/deadline'

/**
 * Prazo de UM pedido do sync, do envio ao corpo lido. Derivação: é o nosso backend (sem
 * Xtream no meio), e o lote maior é o push de 200 itens (~100 KB). Um pedido sem resposta
 * em 15s na rede da TV é tratado como falha de rede: as entradas ficam no outbox e o
 * próximo flush tenta de novo. É o mesmo teto do `request()` do api.ts pro mesmo backend.
 */
export const SYNC_REQUEST_TIMEOUT_MS = 15_000

/** Relógio monotônico pra medir a duração de um pedido (o de parede pula no NTP da TV). */
export function monotonicMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now()
}

/**
 * `fetch` + leitura do corpo com prazo por corrida. LANÇA no prazo (erro de
 * `deadlineError`, ver `isDeadlineError`) e em erro de rede: quem chama já trata falha
 * de rede no `catch`, e o prazo cai no mesmo lugar.
 */
export async function syncRequest<T>(
  url: string,
  init: RequestInit,
  label: string,
  read: (res: Response) => Promise<T>,
): Promise<T> {
  const net = makeNetDeadline(SYNC_REQUEST_TIMEOUT_MS, label)
  try {
    // `exactOptionalPropertyTypes`: só inclui `signal` quando a global existe.
    const reqInit: RequestInit = net.signal ? { ...init, signal: net.signal } : init
    const res = await Promise.race([fetch(url, reqInit), net.expired])
    return await Promise.race([read(res), net.expired])
  } finally {
    net.clear()
  }
}
